import { useCallback } from 'react'
import {
  defineEntitySlotsPreference, validateTimelineOverride, type TimelineOverride,
} from '@solum/experience'
import { useUserSettingPreference } from '../data/useUserSettingPreference'

/** Personal finite Timeline-recipe overrides, keyed by entity and stable slot. */
const KEY = 'connections.timeline'
const SAVE_DEBOUNCE_MS = 300

const readableOverride = (value: unknown): TimelineOverride | null =>
  value != null && validateTimelineOverride(value).length === 0
    ? value as TimelineOverride
    : null

const preference = defineEntitySlotsPreference<TimelineOverride>({
  settingKey: KEY,
  decode: readableOverride,
})

export interface TimelinePreferencesState {
  overrideFor: (slot: string) => TimelineOverride | undefined
  setOverride: (slot: string, value: TimelineOverride) => void
  resetOverride: (slot: string) => void
  ready: boolean
}

export function useTimelinePreferences(entity: string): TimelinePreferencesState {
  const state = useUserSettingPreference(
    preference, { entity }, entity, SAVE_DEBOUNCE_MS,
  )
  const mine = state.value

  const setOverride = useCallback((slot: string, value: TimelineOverride) => {
    if (slot && validateTimelineOverride(value).length === 0) state.update({ slot, value })
  }, [state.update])

  const resetOverride = useCallback((slot: string) => {
    if (slot && slot in mine) state.update({ slot, value: null })
  }, [mine, state.update])

  return {
    overrideFor: useCallback((slot: string) => mine[slot], [mine]),
    setOverride,
    resetOverride,
    ready: state.ready,
  }
}
